import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { useCountryRequest } from '@/common/hooks/useCountryRequest';
import { useLicenseTypeRequest } from '@/common/hooks/useLicenseTypeRequest';
import { DRIVING_LICENSE_REGEX } from '@/common/utils/rules/drivingLicenseRegex.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import {
  DriverInfoTypeForm,
  LicenseType,
} from '@/modules/adjusterReportModules/IncidentParticipants/pages/damaged/propertyDamage/driver-owner/driverInfo/driverInfo.type';
import { driverInfoQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDriverInfo/driverInfoQueryOptions';
import { useDriverLicenceInfoQuery } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchDriverLicenceInfo/driverLicenceInfoQueryOption';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

const CertificateInformation: React.FC = () => {
  const navigate = useNavigate();
  const methods = useFormContext<DriverInfoTypeForm>();
  const [countryName, setCountryName] = React.useState<string>('');
  const { licenseTypeQuery } = useLicenseTypeRequest();
  const { id, damagedId } = useParams();
  const { countryQuery } = useCountryRequest(countryName);
  const { countryQuery: defaultCountryQuery } = useCountryRequest('ایران');

  const selectedLicenseType = methods.watch(
    'certificate_information.license_type',
  );
  const isNoLicense = selectedLicenseType?.label === 'فاقد گواهینامه';

  const handleLicenseTypeChange = (option: LicenseType) => {
    if (option.label !== 'فاقد گواهینامه') return;

    methods.setValue('certificate_information.license_number', '');

    methods.setValue('certificate_information.license_issue_date', null);

    methods.setValue('certificate_information.license_issue_location_country', {
      label: null,
      value: null,
    });
  };

  const searchCountry = (value: string) => {
    setCountryName(value);
  };
  const client = useQueryClient();
  const { mutate: patchDriverLicenceInfo, isPending: loading } =
    useDriverLicenceInfoQuery();
  const onSubmit = (formData: DriverInfoTypeForm) => {
    patchDriverLicenceInfo(
      {
        damaged_id: Number(damagedId),
        id: Number(id),
        data: {
          ...formData.certificate_information,
        },
      },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
              damagedId: +damagedId!,
              id: +id!,
            }).DRIVER_OWNER_INFORMATION.driverContact,
            { replace: true },
          );
        },
      },
    );
  };
  const previousStep = () => {
    methods.clearErrors();
    methods.trigger();
    client.invalidateQueries({
      queryKey: driverInfoQueryOptions({
        damaged_id: Number(damagedId),
        id: Number(id),
      }).queryKey,
    });
    navigate(
      ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
        damagedId: +damagedId!,
        id: +id!,
      }).DRIVER_OWNER_INFORMATION.driver,
      { replace: true },
    );
  };
  return (
    <Flex direction="column" gap={32}>
      <Box>
        <ModalInput
          name="certificate_information.license_type"
          label="نوع گواهی نامه"
          placeholder="انتخاب کنید"
          options={licenseTypeQuery.data || []}
          rules={{ required: requiredText }}
          onChange={handleLicenseTypeChange}
        />
      </Box>
      <Box>
        <TextInput
          label="شماره گواهینامه"
          name="certificate_information.license_number"
          placeholder="شماره گواهینامه را وارد کنید"
          rules={{
            required: isNoLicense ? false : requiredText,
            ...(isNoLicense ? {} : DRIVING_LICENSE_REGEX),
          }}
          max={10}
          noCharInput
          disabled={isNoLicense}
        />
      </Box>
      {!isNoLicense && (
        <Box>
          <DateInput
            label="تاریخ صدور گواهینامه"
            name="certificate_information.license_issue_date"
            buttonText="انتخاب تاریخ"
            modalLabel="انتخاب تاریخ صدور گواهینامه"
            rules={{
              required: isNoLicense ? false : requiredText,
              validate: validateNotFutureDate,
            }}
            disabled={isNoLicense}
          />
        </Box>
      )}
      <Box>
        <ModalInput
          name="certificate_information.license_issue_location_country"
          label="کشور محل صدور گواهینامه"
          placeholder="انتخاب کنید"
          defaultValue={defaultCountryQuery.data?.[0]}
          options={countryQuery.data || []}
          searchOptions={{
            onChange: searchCountry,
            placeholder: 'جستجو کشور',
          }}
          rules={{ required: isNoLicense ? false : requiredText }}
          disabled={isNoLicense}
        />
      </Box>
      <Flex w="100%" gap={16} mb="1rem">
        <Button fullWidth variant="outlined" onClick={previousStep}>
          مرحله قبل
        </Button>{' '}
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Flex>
    </Flex>
  );
};

export default CertificateInformation;
